import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { apiUrl } from '../config/api';
import CameraCapture from '../components/CameraCapture';
import { useEnvironment } from '../hooks/useEnvironment';
import TermsAndConditionsViewer from '../components/TermsAndConditionsViewer';
import PhoneInput, { normalizePhone, phoneLocalDigits } from '../components/UI/PhoneInput';
import DocumentTile, { DOCUMENT_GRID } from '../components/UI/DocumentTile';

const EyeIcon = ({ open }) => (
  open ? (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9-4-9-4s1.5-2.25 4-3.75m3-1.5A9.973 9.973 0 0112 7c5 0 9 4 9 4s-.63.945-1.8 2.025M3 3l18 18" />
    </svg>
  ) : (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  )
);

// The global input rule in index.css is more specific than a utility class,
// so these carry ! to win over it.
const REQ_EMPTY = '!border-rose-300 dark:!border-rose-700/60 focus:!border-rose-500 focus:ring-rose-500/20 !bg-rose-50 dark:!bg-rose-900/20';
const REQ_FILLED = '!border-emerald-300 dark:!border-emerald-700/60 focus:!border-emerald-500 focus:ring-emerald-500/20';
const NEUTRAL = 'border-gray-200 dark:border-slate-600 focus:border-blue-500 focus:ring-blue-500/20';
const baseInputCls = 'w-full !px-2.5 !py-1.5 !text-xs bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-500 rounded text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-400 focus:outline-none focus:ring-2 transition-colors';
const toneCls = (required, filled) => (required ? (filled ? REQ_FILLED : REQ_EMPTY) : NEUTRAL);

// The shared phone field (dial code + number), sized like the other fields.
const phoneBox = '!px-2 !py-1.5 !text-xs !border';
const phoneTone = (filled) => (filled ? '!border-emerald-300 dark:!border-emerald-700/60 !bg-gray-50 dark:!bg-slate-700' : '!border-rose-300 dark:!border-rose-700/60 !bg-rose-50 dark:!bg-rose-900/20');

// Whether a bot-check answer is right: null while empty (or the question is
// still loading). The question is always "What is a + b?"; the server checks
// the answer again.
const botAnswerRight = (question, answer) => {
  const m = /(\d+)\s*\+\s*(\d+)/.exec(question || '');
  const a = String(answer ?? '').trim();
  if (!m || a === '') return null;
  return Number(a) === Number(m[1]) + Number(m[2]);
};

// ✓ or ✗ to the right of a bot-check answer.
const BotMark = ({ right }) => (right == null ? <span className="w-4" /> : (
  <span className={`w-4 text-center text-sm font-bold ${right ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}
    aria-label={right ? 'Right answer' : 'Wrong answer'}>
    {right ? '✓' : '✗'}
  </span>
));

// Marks a field the documents filled in, so the client knows to check it.
// Kept short and on the label's line, so the fields in a row stay aligned.
const FromDocs = ({ show }) => (show ? (
  <span title="Filled in from your documents — please check it"
    className="ml-1.5 min-w-0 truncate normal-case tracking-normal font-medium text-[9px] leading-none px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
    from documents
  </span>
) : null);

// One line, the same height with or without the badge. The label is always
// shown in full; the badge gives way if space is short.
const labelCls = 'flex items-center h-4 mb-1 whitespace-nowrap overflow-hidden text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide';

const Field = ({ label, name, value, onChange, type = 'text', required = true, placeholder, children, autoComplete, prefilled = false, className = '' }) => (
  <div className={className}>
    <label className={labelCls}>
      <span className="flex-shrink-0">{label}</span>
      <FromDocs show={prefilled && String(value ?? '').trim() !== ''} />
    </label>
    {children ? children : (
      <input
        type={type}
        name={name}
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        required={required}
        autoComplete={autoComplete}
        placeholder={placeholder}
        className={`${baseInputCls} ${toneCls(required, String(value ?? '').trim() !== '')}`}
      />
    )}
  </div>
);

const BASE_STEPS = [
  { key: 'account',   title: 'Account' },
  { key: 'personal',  title: 'Personal' },
  { key: 'employment',title: 'Employment' },
  { key: 'banking',   title: 'Banking' },
  { key: 'photo',     title: 'Photo' },
  { key: 'terms',     title: 'Terms & Conditions' },
];

// Registration always starts with the documents; this is the only place they
// are uploaded, and the same files are sent with the registration. With AI on
// they are also read and the form is filled in for the client to check,
// correct and complete.
const PREFILL_STEP = { key: 'prefill', title: 'Your documents' };

const PREFILL_DOCS = [
  { name: 'identificationFile',   type: 'Identification',     label: 'ID or passport',     hint: 'Your identity document' },
  { name: 'proofOfResidenceFile', type: 'Proof of Residence', label: 'Proof of residence', hint: 'A recent utility bill or letter' },
  { name: 'bankStatementFile',    type: 'Bank Statement',     label: 'Bank statement',     hint: 'The last three months' },
  { name: 'payslipFile',          type: 'Payslip',            label: 'Latest payslip',     hint: 'Your most recent one' },
];

const MARITAL_OPTIONS = ['Single', 'Married', 'Divorced', 'Widowed'];
const ACCOUNT_TYPES = ['Cheque', 'Savings', 'Transmission'];

const RegisterClient = () => {
  const { tenantSlug } = useParams();
  const env = useEnvironment();
  const aiEnabled = env?.aiEnabled !== false;
  const STEPS = useMemo(() => [PREFILL_STEP, ...BASE_STEPS], []);

  const [step, setStep] = useState(0);

  // Reading the documents (first step). prefilled holds 'section.field' keys
  // the documents filled in; they are tagged until the client changes them.
  const [prefillStatus, setPrefillStatus] = useState('idle'); // idle | reading | done | failed
  const [prefilled, setPrefilled] = useState(() => new Set());
  const [prefillNotes, setPrefillNotes] = useState([]);
  const [prefillCaptcha, setPrefillCaptcha] = useState({ token: '', question: '', answer: '' });
  // Which documents were last read, so the same ones are not sent again.
  const [readSignature, setReadSignature] = useState('');

  // Account
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Personal / Employment / Banking
  const [personal, setPersonal] = useState({
    fullName: '', gender: '', idPassportNo: '',
    postalAddress: '', postalTelNo: '',
    // Structured residential address — same fields as the staff client form.
    erfNo: '', streetName: '', suburb: '', city: '', country: 'Namibia',
    maritalStatus: '',
  });
  const [employment, setEmployment] = useState({
    occupation: '', salary: '',
    employerName: '', employerPhone: '', employerAddress: '', payslipNumber: '',
  });
  const [banking, setBanking] = useState({
    bankName: '', branchCode: '', accountNumber: '', accountType: '',
  });

  // Files
  const [files, setFiles] = useState({
    identificationFile: null,
    payslipFile: null,
    bankStatementFile: null,
    whatsAppPhotoFile: null,
    proofOfResidenceFile: null,
  });

  // Terms & Conditions agreement (required on the final step). The box only
  // unlocks once the terms have been scrolled to the end.
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [termsRead, setTermsRead] = useState(false);
  const markTermsRead = useCallback(() => setTermsRead(true), []);

  // Captcha
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaQuestion, setCaptchaQuestion] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(false);
  // The bot check is answered once, on the first step, whether the documents
  // are read or not; the pass it gives covers reading them and creating the
  // account. Only if the pass runs out (2 hours) is it asked again, on the
  // last step.
  const [botPass, setBotPass] = useState('');
  const [passExpired, setPassExpired] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loginPath = tenantSlug ? `/${tenantSlug}/login` : '/login';

  const untag = (key) => setPrefilled((prev) => {
    if (!prev.has(key)) return prev;
    const next = new Set(prev);
    next.delete(key);
    return next;
  });
  const setPersonalField = (k, v) => { untag(`personal.${k}`); setPersonal((p) => ({ ...p, [k]: v })); };
  const setEmploymentField = (k, v) => { untag(`employment.${k}`); setEmployment((p) => ({ ...p, [k]: v })); };
  const setBankingField = (k, v) => { untag(`banking.${k}`); setBanking((p) => ({ ...p, [k]: v })); };
  const isPrefilled = (key) => prefilled.has(key);

  // Live password requirements (matches the Change Password page).
  const passwordRules = useMemo(() => ([
    { label: 'At least 8 characters long', ok: password.length >= 8 },
    { label: 'Contains at least one uppercase letter (A–Z)', ok: /[A-Z]/.test(password) },
    { label: 'Contains at least one lowercase letter (a–z)', ok: /[a-z]/.test(password) },
    { label: 'Contains at least one number (0–9)', ok: /[0-9]/.test(password) },
    { label: 'Contains at least one special character (e.g. ! @ # $ % ^ & *)', ok: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password) },
    { label: 'Passwords match', ok: confirmPassword.length > 0 && confirmPassword === password },
  ]), [password, confirmPassword]);
  const allPasswordRulesPass = passwordRules.every((r) => r.ok);

  const loadCaptcha = useCallback(async () => {
    setCaptchaLoading(true);
    try {
      const res = await axios.get(apiUrl('/api/auth/captcha'));
      setCaptchaToken(res.data.token);
      setCaptchaQuestion(res.data.question);
      setCaptchaAnswer('');
    } catch {
      setCaptchaQuestion('Failed to load captcha. Click refresh.');
    } finally {
      setCaptchaLoading(false);
    }
  }, []);

  // Load the captcha when entering the Terms & Conditions step.
  useEffect(() => {
    if (STEPS[step]?.key === 'terms' && passExpired && !botPass && !captchaToken) loadCaptcha();
  }, [step, passExpired, botPass, captchaToken, loadCaptcha, STEPS]);

  // The document-reading step has its own captcha: each one works once.
  const loadPrefillCaptcha = useCallback(async () => {
    try {
      const res = await axios.get(apiUrl('/api/auth/captcha'));
      setPrefillCaptcha({ token: res.data.token, question: res.data.question, answer: '' });
    } catch {
      setPrefillCaptcha({ token: '', question: 'Failed to load the question. Click refresh.', answer: '' });
    }
  }, []);
  useEffect(() => {
    if (STEPS[step]?.key === 'prefill' && !botPass && !prefillCaptcha.token) loadPrefillCaptcha();
  }, [botPass, step, prefillCaptcha.token, loadPrefillCaptcha, STEPS]);

  const pickDocument = (name, f) => {
    if (f && f.type !== 'application/pdf') { setError('Only PDF files are allowed for documents (the photo is taken with your camera).'); return; }
    if (f && f.size > 10 * 1024 * 1024) { setError('Each document must be 10 MB or smaller.'); return; }
    setError('');
    setFiles((p) => ({ ...p, [name]: f }));
  };

  const docsSignature = PREFILL_DOCS
    .map((d) => (files[d.name] ? `${d.name}:${files[d.name].name}:${files[d.name].size}:${files[d.name].lastModified}` : ''))
    .join('|');
  const alreadyRead = prefillStatus === 'done' && readSignature === docsSignature;
  const missingDocs = PREFILL_DOCS.filter((d) => !files[d.name]);
  const checkAllDocs = () => {
    if (!missingDocs.length) return true;
    setError(`Please upload: ${missingDocs.map((d) => d.label).join(', ')}.`);
    return false;
  };
  // Go on without reading the documents; the client fills in the form.
  const [checkingBot, setCheckingBot] = useState(false);
  const needBotAnswer = () => {
    if (botPass) return false;
    if (!prefillCaptcha.token || String(prefillCaptcha.answer).trim() === '') {
      if (!prefillCaptcha.token) loadPrefillCaptcha();
      setError('Please answer the bot-check question.');
      return true;
    }
    if (botAnswerRight(prefillCaptcha.question, prefillCaptcha.answer) === false) {
      setError("That bot-check answer isn't right. Please try again.");
      return true;
    }
    return false;
  };
  const fillInMyself = async () => {
    if (!checkAllDocs()) return;
    if (needBotAnswer()) return;
    setError('');
    if (!botPass) {
      setCheckingBot(true);
      try {
        const res = await axios.post(apiUrl('/api/clients/self-register/bot-check'), {
          captchaToken: prefillCaptcha.token,
          captchaAnswer: prefillCaptcha.answer,
        });
        setBotPass(res.data?.botPass || '');
        setPassExpired(false);
        setPrefillCaptcha({ token: '', question: '', answer: '' });
      } catch (err) {
        setError(err.response?.data?.error || 'The bot check could not be confirmed. Please try again.');
        loadPrefillCaptcha();
        return;
      } finally {
        setCheckingBot(false);
      }
    }
    if (prefillStatus === 'failed') setPrefillStatus('idle');
    setStep((st) => st + 1);
  };

  // Reads the uploaded documents and fills the form with what was found.
  const readDocuments = async () => {
    // Already read, and nothing changed: go on without another read.
    if (alreadyRead) { setError(''); setStep((st) => st + 1); return; }
    if (!checkAllDocs()) return;
    const chosen = PREFILL_DOCS;
    if (needBotAnswer()) return;
    setError('');
    setPrefillStatus('reading');
    try {
      const fd = new FormData();
      chosen.forEach((d) => fd.append(d.name, files[d.name]));
      if (botPass) {
        fd.append('botPass', botPass);
      } else {
        fd.append('captchaToken', prefillCaptcha.token);
        fd.append('captchaAnswer', prefillCaptcha.answer);
      }
      const res = await axios.post(apiUrl('/api/clients/self-register/extract'), fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = res.data?.data || {};
      const p = data.personal_info || {};
      const o = data.occupation_info || {};
      const b = data.bank_info || {};
      const clean = (v) => String(v ?? '').trim();
      const found = new Set();

      const personalNext = {};
      const putP = (k, v) => { const t = clean(v); if (t) { personalNext[k] = t; found.add(`personal.${k}`); } };
      putP('fullName', [p.first_name, p.middle_name, p.last_name].map(clean).filter(Boolean).join(' '));
      putP('gender', ['Male', 'Female', 'Other'].includes(clean(p.gender)) ? p.gender : '');
      putP('idPassportNo', p.id_passport_no);
      putP('postalAddress', p.postal_address);
      putP('postalTelNo', normalizePhone(p.phone_number));
      putP('erfNo', p.erf_no);
      putP('streetName', p.street_name);
      putP('suburb', p.suburb);
      putP('city', p.city);
      putP('country', p.country);
      putP('maritalStatus', MARITAL_OPTIONS.includes(clean(p.marital_status)) ? p.marital_status : '');

      const employmentNext = {};
      const putE = (k, v) => { const t = clean(v); if (t) { employmentNext[k] = t; found.add(`employment.${k}`); } };
      putE('occupation', o.occupation);
      putE('salary', Number(o.salary) > 0 ? String(Number(o.salary)) : '');
      putE('employerName', o.employer_name);
      putE('employerPhone', normalizePhone(o.employer_phone));
      putE('employerAddress', o.employer_address);
      putE('payslipNumber', o.payslip_number);

      const bankingNext = {};
      const putB = (k, v) => { const t = clean(v); if (t) { bankingNext[k] = t; found.add(`banking.${k}`); } };
      putB('bankName', b.bank_name);
      putB('branchCode', b.branch_code);
      putB('accountNumber', b.account_number);
      putB('accountType', ACCOUNT_TYPES.includes(clean(b.account_type)) ? b.account_type : '');

      setPersonal((prev) => ({ ...prev, ...personalNext }));
      setEmployment((prev) => ({ ...prev, ...employmentNext }));
      setBanking((prev) => ({ ...prev, ...bankingNext }));
      const docEmail = clean(p.email);
      if (docEmail && !email) { setEmail(docEmail); found.add('account.email'); }
      setPrefilled(found);
      setPrefillNotes(Array.isArray(data.notes) ? data.notes : []);
      setPrefillStatus('done');
      setReadSignature(docsSignature);
      setPrefillCaptcha({ token: '', question: '', answer: '' });
      if (res.data?.botPass) { setBotPass(res.data.botPass); setPassExpired(false); }
      setStep((st) => st + 1);
    } catch (err) {
      if (err.response?.data?.botPass) { setBotPass(err.response.data.botPass); setPassExpired(false); }
      // The pass ran out: answer the question again here.
      if (botPass && err.response?.data?.code === 'CAPTCHA') setBotPass('');
      setPrefillStatus('failed');
      setError(err.response?.data?.error || 'We could not read your documents. You can fill in the form yourself.');
      loadPrefillCaptcha();
    }
  };

  // What is still missing on a step ('' when it is complete). Used to check a
  // step before moving on, and to show a step as done only when it really is.
  const stepProblem = (idx) => {
    const cur = STEPS[idx].key;
    if (cur === 'prefill') {
      if (missingDocs.length) return `Please upload: ${missingDocs.map((d) => d.label).join(', ')}.`;
      // A right answer not yet confirmed counts: Create account confirms it.
      if (!botPass && !passExpired && botAnswerRight(prefillCaptcha.question, prefillCaptcha.answer) !== true) {
        return 'Please answer the bot-check question.';
      }
      return '';
    }
    if (cur === 'account') {
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'A valid email is required.';
      if (!allPasswordRulesPass) return 'Please ensure your password meets all requirements.';
      return '';
    }
    if (cur === 'personal') {
      const labels = {
        fullName: 'full name', gender: 'gender', idPassportNo: 'ID/Passport No',
        postalAddress: 'postal address', postalTelNo: 'phone number',
        erfNo: 'ERF no', streetName: 'street name', suburb: 'location/suburb', city: 'city/town', country: 'country',
        maritalStatus: 'marital status',
      };
      for (const [k, label] of Object.entries(labels)) {
        if (!String(personal[k] || '').trim()) return `Please fill in: ${label}`;
      }
      if (phoneLocalDigits(personal.postalTelNo).length < 8) return 'Please enter your full phone number.';
      return '';
    }
    if (cur === 'employment') {
      const need = ['occupation', 'salary', 'employerName', 'employerPhone', 'employerAddress', 'payslipNumber'];
      for (const k of need) {
        if (!String(employment[k] || '').trim()) return `Please fill in: ${k}`;
      }
      if (phoneLocalDigits(employment.employerPhone).length < 8) return "Please enter the employer's full phone number.";
      if (!(Number(employment.salary) > 0)) return 'Please enter a valid monthly salary (greater than 0).';
      return '';
    }
    if (cur === 'banking') {
      const missing = Object.entries(banking).find(([, v]) => !String(v || '').trim());
      if (missing) return `Please fill in: ${missing[0]}`;
      return '';
    }
    if (cur === 'terms') {
      if (!termsRead) return 'Please scroll to the end of the Terms & Conditions.';
      if (!agreedTerms) return 'Please tick that you agree to the Terms & Conditions.';
      if (!botPass && passExpired && (!captchaToken || captchaAnswer === '')) return 'Please answer the bot-check question.';
      if (!botPass && passExpired && botAnswerRight(captchaQuestion, captchaAnswer) === false) return "That bot-check answer isn't right. Please try again.";
      return '';
    }
    if (cur === 'photo') {
      if (!files.whatsAppPhotoFile) return 'Please take your selfie.';
      return '';
    }
    return '';
  };

  const validateStep = (idx = step) => {
    const problem = stepProblem(idx);
    setError(problem);
    return !problem;
  };

  const next = () => { if (validateStep()) { setStep((s) => Math.min(s + 1, STEPS.length - 1)); setError(''); } };
  const prev = () => { setStep((s) => Math.max(s - 1, 0)); setError(''); };

  // Steps can be visited in any order, so every one is checked before the
  // account is created: the first incomplete one is opened with what is
  // missing on it, and any others are named.
  const submit = async (e) => {
    e.preventDefault();
    const incomplete = STEPS
      .map((st, i) => ({ i, title: st.title, problem: stepProblem(i) }))
      .filter((x) => x.problem);
    if (incomplete.length) {
      const [first, ...rest] = incomplete;
      setStep(first.i);
      const others = rest.length ? ` Also still to complete: ${rest.map((r) => r.title).join(', ')}.` : '';
      setError(`${first.title}: ${first.problem}${others}`);
      return;
    }
    setSubmitting(true);
    setError('');
    setSuccess('');
    // The bot check was answered on the first step but not yet confirmed.
    let pass = botPass;
    if (!pass && !passExpired) {
      try {
        const res = await axios.post(apiUrl('/api/clients/self-register/bot-check'), {
          captchaToken: prefillCaptcha.token,
          captchaAnswer: prefillCaptcha.answer,
        });
        pass = res.data?.botPass || '';
        setBotPass(pass);
        setPrefillCaptcha({ token: '', question: '', answer: '' });
      } catch (err) {
        setSubmitting(false);
        setStep(0);
        setError(`Your documents: ${err.response?.data?.error || 'The bot check could not be confirmed. Please answer it again.'}`);
        loadPrefillCaptcha();
        return;
      }
    }
    try {
      const fd = new FormData();
      fd.append('email', email);
      fd.append('password', password);
      fd.append('confirmPassword', confirmPassword);
      Object.entries(personal).forEach(([k, v]) => fd.append(k, v));
      Object.entries(employment).forEach(([k, v]) => fd.append(k, v));
      Object.entries(banking).forEach(([k, v]) => fd.append(k, v));
      Object.entries(files).forEach(([k, file]) => { if (file) fd.append(k, file); });
      if (pass) {
        fd.append('botPass', pass);
      } else {
        fd.append('captchaToken', captchaToken);
        fd.append('captchaAnswer', captchaAnswer);
      }
      if (tenantSlug) fd.append('tenantSlug', tenantSlug);

      const res = await axios.post(apiUrl('/api/clients/self-register'), fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess(res.data?.message || 'Account created. Check your inbox to verify your email.');
    } catch (err) {
      // The pass ran out: ask the question here instead.
      if (pass && err.response?.data?.code === 'CAPTCHA') {
        setBotPass('');
        setPassExpired(true);
        setError('Please answer the bot-check question, then create your account.');
      } else {
        setError(err.response?.data?.error || 'Registration failed. Please try again.');
      }
      loadCaptcha();
    } finally {
      setSubmitting(false);
    }
  };

  const StepHeader = () => (
    <div className="flex items-center gap-1 sm:gap-1.5 mb-4 overflow-x-auto">
      {STEPS.map((s, i) => (
        <React.Fragment key={s.key}>
          {i > 0 && (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 text-gray-300 dark:text-slate-600">
              <path d="m9 18 6-6-6-6" />
            </svg>
          )}
          <button
            type="button"
            onClick={() => { if (!submitting && prefillStatus !== 'reading') { setError(''); setStep(i); } }}
            aria-current={i === step ? 'step' : undefined}
            className={`flex-1 text-center px-2 py-2 rounded-lg text-[11px] sm:text-xs font-semibold transition-colors cursor-pointer hover:ring-2 hover:ring-blue-200 dark:hover:ring-blue-800 ${
              i === step
                ? 'bg-blue-600 text-white shadow-sm'
                : stepProblem(i) === ''
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800/40'
                : 'bg-gray-100 text-gray-500 border border-gray-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600'
            }`}
          >
            {i !== step && stepProblem(i) === '' && <span className="mr-1">✓</span>}
            {s.title}
          </button>
        </React.Fragment>
      ))}
    </div>
  );

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 p-4">
        <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-xl shadow-lg overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500" />
          <div className="px-6 py-8 text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center text-2xl">✅</div>
            <h1 className="text-xl font-extrabold text-blue-800 dark:text-blue-300 mb-3">Account created</h1>
            <p className="text-sm text-gray-700 dark:text-slate-200 mb-6">{success}</p>
            <Link to={loginPath} className="inline-block px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white hover:text-white dark:text-white dark:hover:text-white text-sm font-semibold">
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-start justify-center bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 p-4 pt-8 sm:pt-16">
      <div className="w-full max-w-5xl bg-white dark:bg-slate-800 rounded-xl shadow-lg overflow-hidden">
        <div className="h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500" />
        <div className="px-6 py-6 sm:px-8 sm:py-7">
          <h1 className="text-xl font-extrabold text-blue-800 dark:text-blue-300 text-center mb-1">Create your account</h1>
          <p className="text-[11px] text-gray-500 dark:text-slate-400 text-center mb-5">
            Register to apply for loans. We'll send a verification link to your inbox; an administrator must approve your account before you can sign in.
          </p>

          <StepHeader />

          {prefillStatus === 'done' && ['account', 'personal', 'employment', 'banking'].includes(STEPS[step]?.key) && (
            <div className="mb-4 p-3 rounded-lg border border-rose-200 dark:border-rose-800/50 bg-rose-50 dark:bg-rose-900/20 text-xs text-rose-900 dark:text-rose-200">
              <p className="font-semibold">
                {prefilled.size > 0
                  ? `We filled in ${prefilled.size} detail${prefilled.size === 1 ? '' : 's'} from your documents.`
                  : 'We could not find your details in the documents.'}
              </p>
              <p className="mt-0.5 text-[11px] text-rose-800 dark:text-rose-300">
                Please check every field marked <em>from documents</em> and correct anything that is wrong, then fill in
                the fields highlighted in red — they are required before you can continue.
              </p>
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 rounded border border-red-200 bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800/40 text-xs flex flex-wrap items-center justify-between gap-3">
              <span className="min-w-0 flex-1">{error}</span>
              {STEPS[step]?.key === 'prefill' && prefillStatus === 'failed' && (
                <button type="button" onClick={fillInMyself}
                  className="flex-shrink-0 px-3 py-1.5 rounded-lg bg-white dark:bg-slate-800 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 text-[11px] font-semibold hover:bg-red-100 dark:hover:bg-slate-700">
                  Fill in the form myself →
                </button>
              )}
            </div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); if (step < STEPS.length - 1) next(); else submit(e); }} className="space-y-3">
            {/* ── Step 0: Your documents (read to fill in the form) ── */}
            {STEPS[step].key === 'prefill' && (
              <>
                <div className="p-3 rounded-lg border border-blue-200 dark:border-blue-800/50 bg-blue-50 dark:bg-blue-900/20">
                  <p className="text-xs font-semibold text-blue-900 dark:text-blue-200">Start with your documents</p>
                  <p className="mt-0.5 text-[11px] text-blue-800 dark:text-blue-300">
                    {aiEnabled
                      ? 'Upload all four documents and we will read them and fill in your form for you. You then check the details, correct anything that is wrong and fill in what is missing. '
                      : 'Upload all four documents. '}
                    These are the documents saved with your registration — you will not be asked for them again.
                  </p>
                </div>
                <p className="text-[11px] text-gray-500 dark:text-slate-400">PDF files only, max 10 MB each. All four are required.</p>
                <div className={DOCUMENT_GRID}>
                  {PREFILL_DOCS.map((doc) => (
                    <DocumentTile
                      key={doc.name}
                      type={doc.type}
                      label={doc.label}
                      hint={doc.hint}
                      file={files[doc.name]}
                      onPick={(f) => pickDocument(doc.name, f)}
                      onRemove={() => setFiles((p) => ({ ...p, [doc.name]: null }))}
                      disabled={prefillStatus === 'reading'}
                    />
                  ))}
                </div>

                {botPass ? (
                  <p className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">✓ Bot check done</p>
                ) : (
                <div className="rounded-lg border border-gray-200 dark:border-slate-600 p-3 bg-gray-50 dark:bg-slate-700/40">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Bot check</label>
                    <button type="button" onClick={loadPrefillCaptcha} disabled={prefillStatus === 'reading'}
                      className="px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 disabled:opacity-50">↻ Refresh</button>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-semibold text-gray-800 dark:text-slate-100 flex-1">{prefillCaptcha.question || 'Loading…'}</span>
                    <input type="text" inputMode="numeric" value={prefillCaptcha.answer}
                      onChange={(e) => setPrefillCaptcha((c) => ({ ...c, answer: e.target.value }))}
                      className={`w-24 text-center !px-2 !py-1.5 !text-xs ${toneCls(true, botAnswerRight(prefillCaptcha.question, prefillCaptcha.answer) === true)}`}
                      placeholder="Answer" disabled={prefillStatus === 'reading'} />
                    <BotMark right={botAnswerRight(prefillCaptcha.question, prefillCaptcha.answer)} />
                  </div>
                </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3">
                  {aiEnabled ? (
                    <>
                      <button type="button" onClick={fillInMyself} disabled={prefillStatus === 'reading' || checkingBot}
                        className="text-xs font-semibold text-gray-600 dark:text-slate-300 hover:underline disabled:opacity-50">
                        I'll fill in the form myself
                      </button>
                      <button type="button" onClick={readDocuments} disabled={prefillStatus === 'reading'}
                        className="px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold disabled:opacity-60">
                        {prefillStatus === 'reading'
                          ? 'Reading your documents… (up to a minute)'
                          : alreadyRead ? 'Documents read — continue' : 'Read my documents'}
                      </button>
                    </>
                  ) : (
                    <button type="button" onClick={fillInMyself} disabled={checkingBot}
                      className="ml-auto px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold">
                      Continue →
                    </button>
                  )}
                </div>
              </>
            )}

            {/* ── Step 1: Account ── */}
            {STEPS[step].key === 'account' && (
              <>
                <Field label="Email" name="email" type="email" value={email} onChange={(_, v) => { untag('account.email'); setEmail(v); }} autoComplete="email" placeholder="you@example.com" prefilled={isPrefilled('account.email')} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">Password</label>
                  <div className="relative">
                    <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="new-password"
                      className={`${baseInputCls} pr-10 ${toneCls(true, password.trim() !== '')}`}
                    />
                    <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 dark:text-slate-400 hover:text-gray-800" aria-label="Toggle password visibility">
                      <EyeIcon open={showPassword} />
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">Confirm password</label>
                  <div className="relative">
                    <input type={showConfirm ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required autoComplete="new-password"
                      className={`${baseInputCls} pr-10 ${toneCls(true, confirmPassword.trim() !== '')}`}
                    />
                    <button type="button" onClick={() => setShowConfirm((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 dark:text-slate-400 hover:text-gray-800" aria-label="Toggle confirm visibility">
                      <EyeIcon open={showConfirm} />
                    </button>
                  </div>
                </div>
                </div>
                <div className="p-3 rounded border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-[11px]">
                  Your password must meet all of the following requirements:
                  <ul className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
                    {passwordRules.map((r, i) => (
                      <li key={i} className={`flex items-center gap-2 ${r.ok ? 'text-green-700 dark:text-green-400' : 'text-gray-600 dark:text-slate-400'}`}>
                        <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[11px] font-bold ${r.ok ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400' : 'bg-gray-200 dark:bg-slate-600 text-gray-600 dark:text-slate-400'}`}>
                          {r.ok ? '✓' : '•'}
                        </span>
                        <span>{r.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}

            {/* ── Step 2: Personal ── */}
            {STEPS[step].key === 'personal' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <Field label="Full name" name="fullName" value={personal.fullName} onChange={setPersonalField} placeholder="First Middle Last" prefilled={isPrefilled('personal.fullName')} />
                <Field label="Gender" name="gender" value={personal.gender} onChange={setPersonalField} prefilled={isPrefilled('personal.gender')}>
                  <select value={personal.gender} onChange={(e) => setPersonalField('gender', e.target.value)} required
                    className={`${baseInputCls} ${toneCls(true, personal.gender !== '')}`}>
                    <option value="">Select…</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </Field>
                <Field label="ID / Passport No" name="idPassportNo" value={personal.idPassportNo} onChange={setPersonalField} prefilled={isPrefilled('personal.idPassportNo')} />
                <Field label="Postal address" name="postalAddress" value={personal.postalAddress} onChange={setPersonalField} prefilled={isPrefilled('personal.postalAddress')} />
                <Field label="Phone number" name="postalTelNo" value={phoneLocalDigits(personal.postalTelNo)} onChange={setPersonalField} prefilled={isPrefilled('personal.postalTelNo')}>
                  <PhoneInput value={personal.postalTelNo} onChange={(v) => setPersonalField('postalTelNo', v)} required
                    selectClassName={`${phoneBox} ${phoneTone(phoneLocalDigits(personal.postalTelNo).length >= 8)}`}
                    inputClassName={`${phoneBox} ${phoneTone(phoneLocalDigits(personal.postalTelNo).length >= 8)}`} />
                </Field>

                <Field label="Marital status" name="maritalStatus" value={personal.maritalStatus} onChange={setPersonalField} prefilled={isPrefilled('personal.maritalStatus')}>
                  <select value={personal.maritalStatus} onChange={(e) => setPersonalField('maritalStatus', e.target.value)} required
                    className={`${baseInputCls} ${toneCls(true, personal.maritalStatus !== '')}`}>
                    <option value="">Select…</option>
                    <option value="Single">Single</option>
                    <option value="Married">Married</option>
                    <option value="Divorced">Divorced</option>
                    <option value="Widowed">Widowed</option>
                  </select>
                </Field>
                {/* Structured residential address — mirrors the staff client form */}
                <div className="md:col-span-2 lg:col-span-3">
                  <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1 border-t border-gray-100 dark:border-slate-700 pt-2">
                    Residential address
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <div>
                      <span className={labelCls}><span className="flex-shrink-0">ERF no</span><FromDocs show={isPrefilled('personal.erfNo') && String(personal.erfNo ?? '').trim() !== ''} /></span>
                      <input type="text" value={personal.erfNo} onChange={(e) => setPersonalField('erfNo', e.target.value)} required
                        className={`${baseInputCls} ${toneCls(true, String(personal.erfNo ?? '').trim() !== '')}`} />
                    </div>
                    <div>
                      <span className={labelCls}><span className="flex-shrink-0">Street name</span><FromDocs show={isPrefilled('personal.streetName') && String(personal.streetName ?? '').trim() !== ''} /></span>
                      <input type="text" value={personal.streetName} onChange={(e) => setPersonalField('streetName', e.target.value)} required
                        className={`${baseInputCls} ${toneCls(true, String(personal.streetName ?? '').trim() !== '')}`} />
                    </div>
                    <div>
                      <span className={labelCls}><span className="flex-shrink-0">Location / Suburb</span><FromDocs show={isPrefilled('personal.suburb') && String(personal.suburb ?? '').trim() !== ''} /></span>
                      <input type="text" value={personal.suburb} onChange={(e) => setPersonalField('suburb', e.target.value)} required
                        className={`${baseInputCls} ${toneCls(true, String(personal.suburb ?? '').trim() !== '')}`} />
                    </div>
                    <div>
                      <span className={labelCls}><span className="flex-shrink-0">City / Town</span><FromDocs show={isPrefilled('personal.city') && String(personal.city ?? '').trim() !== ''} /></span>
                      <input type="text" value={personal.city} onChange={(e) => setPersonalField('city', e.target.value)} required
                        className={`${baseInputCls} ${toneCls(true, String(personal.city ?? '').trim() !== '')}`} />
                    </div>
                    <div>
                      <span className={labelCls}><span className="flex-shrink-0">Country</span><FromDocs show={isPrefilled('personal.country') && String(personal.country ?? '').trim() !== ''} /></span>
                      <input type="text" value={personal.country} onChange={(e) => setPersonalField('country', e.target.value)} required
                        className={`${baseInputCls} ${toneCls(true, String(personal.country ?? '').trim() !== '')}`} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── Step 3: Employment ── */}
            {STEPS[step].key === 'employment' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <Field label="Occupation" name="occupation" value={employment.occupation} onChange={setEmploymentField} prefilled={isPrefilled('employment.occupation')} />
                <Field label="Take-home pay (N$ a month)" name="salary" type="number" value={employment.salary} onChange={setEmploymentField} placeholder="Net pay after deductions" prefilled={isPrefilled('employment.salary')} />
                <Field label="Employer name" name="employerName" value={employment.employerName} onChange={setEmploymentField} prefilled={isPrefilled('employment.employerName')} />
                <Field label="Employer phone" name="employerPhone" value={phoneLocalDigits(employment.employerPhone)} onChange={setEmploymentField} prefilled={isPrefilled('employment.employerPhone')}>
                  <PhoneInput value={employment.employerPhone} onChange={(v) => setEmploymentField('employerPhone', v)} required
                    selectClassName={`${phoneBox} ${phoneTone(phoneLocalDigits(employment.employerPhone).length >= 8)}`}
                    inputClassName={`${phoneBox} ${phoneTone(phoneLocalDigits(employment.employerPhone).length >= 8)}`} />
                </Field>
                <Field label="Employer address" name="employerAddress" value={employment.employerAddress} onChange={setEmploymentField} prefilled={isPrefilled('employment.employerAddress')} />
                <Field label="Payslip number" name="payslipNumber" value={employment.payslipNumber} onChange={setEmploymentField} prefilled={isPrefilled('employment.payslipNumber')} />
              </div>
            )}

            {/* ── Step 4: Banking ── */}
            {STEPS[step].key === 'banking' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <Field label="Bank name" name="bankName" value={banking.bankName} onChange={setBankingField} prefilled={isPrefilled('banking.bankName')} />
                <Field label="Branch code" name="branchCode" value={banking.branchCode} onChange={setBankingField} prefilled={isPrefilled('banking.branchCode')} />
                <Field label="Account number" name="accountNumber" value={banking.accountNumber} onChange={setBankingField} prefilled={isPrefilled('banking.accountNumber')} />
                <Field label="Account type" name="accountType" value={banking.accountType} onChange={setBankingField} prefilled={isPrefilled('banking.accountType')}>
                  <select value={banking.accountType} onChange={(e) => setBankingField('accountType', e.target.value)} required
                    className={`${baseInputCls} ${toneCls(true, banking.accountType !== '')}`}>
                    <option value="">Select…</option>
                    <option value="Cheque">Cheque</option>
                    <option value="Savings">Savings</option>
                    <option value="Transmission">Transmission</option>
                  </select>
                </Field>
              </div>
            )}

            {/* ── Terms & Conditions (last step): read to the end, agree, bot check ── */}
            {STEPS[step].key === 'terms' && (
              <>
                <TermsAndConditionsViewer readToEnd={termsRead} onReadToEnd={markTermsRead} />
                <label className={`flex items-start gap-2.5 text-xs text-gray-700 dark:text-slate-300 ${termsRead ? 'cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}
                  title={termsRead ? undefined : 'Scroll to the end of the Terms & Conditions first'}>
                  <input
                    type="checkbox"
                    checked={agreedTerms}
                    onChange={(e) => setAgreedTerms(e.target.checked)}
                    disabled={submitting || !termsRead}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
                  />
                  <span>
                    I have read and agree to the Terms &amp; Conditions, including the processing and sharing of my
                    information described in them.
                  </span>
                </label>
                {!botPass && passExpired && (
                <div className="rounded-lg border border-gray-200 dark:border-slate-600 p-3 bg-gray-50 dark:bg-slate-700/40 mt-2">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Bot check</label>
                    <button type="button" onClick={loadCaptcha} disabled={captchaLoading || submitting}
                      className="px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 disabled:opacity-50">↻ Refresh</button>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-semibold text-gray-800 dark:text-slate-100 flex-1">{captchaQuestion || 'Loading…'}</span>
                    <input type="text" inputMode="numeric" value={captchaAnswer}
                      onChange={(e) => setCaptchaAnswer(e.target.value)} required
                      className={`w-24 text-center !px-2 !py-1.5 !text-xs ${toneCls(true, botAnswerRight(captchaQuestion, captchaAnswer) === true)}`}
                      placeholder="Answer" disabled={submitting || captchaLoading} />
                    <BotMark right={botAnswerRight(captchaQuestion, captchaAnswer)} />
                  </div>
                </div>
                )}
              </>
            )}

            {/* ── Photo: the selfie (documents were uploaded on the first step) ── */}
            {STEPS[step].key === 'photo' && (
              <>
                {/* Client photo — selfie captured via webcam (same component the loan application uses) */}
                <div>
                  <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
                    Client photo
                  </label>
                  <div onClick={(e) => e.preventDefault()}>
                    <CameraCapture
                      onPhotoCapture={(file) => setFiles((p) => ({ ...p, whatsAppPhotoFile: file }))}
                      buttonText="Take selfie"
                    />
                  </div>
                  {files.whatsAppPhotoFile && (
                    <div className="mt-2 p-2 rounded border border-green-200 dark:border-green-700/40 bg-green-50 dark:bg-green-900/20 text-[11px] text-green-700 dark:text-green-300">
                      ✓ Photo captured.
                    </div>
                  )}
                </div>


              </>
            )}

            {/* Nav */}
            <div className="flex items-center justify-between pt-2 gap-2">
              <button type="button" onClick={prev} disabled={step === 0 || submitting}
                className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 hover:bg-gray-200 dark:hover:bg-slate-600 disabled:opacity-40 text-xs font-semibold">
                ← Previous
              </button>
              {STEPS[step].key === 'prefill' ? null : step < STEPS.length - 1 ? (
                <button type="button" onClick={next} disabled={submitting}
                  className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold disabled:opacity-60">
                  Next →
                </button>
              ) : (
                <button type="submit" disabled={submitting || !agreedTerms}
                  title={!agreedTerms ? 'Please agree to the Terms & Conditions first' : undefined}
                  className="px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold disabled:opacity-60">
                  {submitting ? 'Submitting…' : 'Create account'}
                </button>
              )}
            </div>
          </form>

          <div className="mt-5 text-center text-xs text-gray-500 dark:text-slate-400">
            Already have an account?{' '}
            <Link to={loginPath} className="text-blue-700 dark:text-blue-300 font-semibold hover:underline">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterClient;
